import { BasePlugin } from '@uppy/core';
import getSocketHost from '@uppy/utils/lib/getSocketHost';
import { RateLimitedQueue } from '@uppy/utils/lib/RateLimitedQueue';
import { Provider, RequestClient, Socket } from '@uppy/companion-client';
import uuid from 'react-uuid';

import sanitizeFileName from '../../utils/sanitizeFileName';

const defaultOptions = {
  limit: 20
};
export default class RemoteImageDownloader extends BasePlugin {
  #queueRequestSocketToken;

  constructor(uppy, opts) {
    super(uppy, opts);

    this.opts = { ...defaultOptions, ...opts };

    // these hold sockets and files info for multi uploads
    this.sockets = {};
    this.files = {};

    this.id = this.opts.id || 'RemoteImageDownloader';
    this.type = 'modifier';

    // Rate limited requests queue that is across all uploads with this plugin
    this.requests = new RateLimitedQueue(this.opts.limit);
    this.#queueRequestSocketToken = this.requests.wrapPromiseFunction(this.#requestSocketToken);
  }

  /**
   * @param  {Object} file - A file to download
   * @returns  {Object|null} - Added file or null
   */
  addFile = async (file) => {
    // Only for remote images
    if (file.isRemote && file.type.startsWith('image/')) {
      file.meta.source = file.source;
      try {
        // Uses companion to upload a file to TUS server and gets an uploaded file URL
        // Then fetches a blob from the URL and sets it to file.data
        // The file blob is used in ImageEditor, Compressor and ThumbnailGenerator plugins
        const { url } = await this.#uploadRemoteFile(file);
        if (url) {
          return await this.#fetchFile(file, url);
        }
        throw new Error('Cannot add a file');
      } catch (err) {
        this.#loadingStatus?.hide();
        this.uppy.emit('upload-error', file, err);
        this.uppy.log(`[RemoteImageDownloader] ${err.message}, ${err.stack}`, 'error');
        this.uppy.info('Failed to fetch this file', 'error');
        return null;
      }
    }
    return file;
  };

  #fetchFile = async (file, url) => {
    let addedFile;
    const response = await fetch(url, {
      headers: this.opts.headers,
    });
    const blob = await response.blob();
    const currentFile = {
      ...file,
      data: blob,
      isRemote: false,
      preview: null,
      serverToken: null
    };

    try {
      const addedFileId = this.uppy.addFile(currentFile);
      addedFile = this.uppy.getFile(addedFileId);
      this.uppy.emit('thumbnail:request', addedFile);
    } catch (err) {
      // Catch Uppy errors to allow for specific messaging to users about file format violations
      // such as filesize or type, then re-throw the error.
      this.uppy.info(err.message, 'error');
      throw (err);
    }

    this.#loadingStatus?.hide();
    return addedFile;
  };

  #setFileState(fileId, attributes) {
    if (!this.files[fileId]) {
      throw new Error(`Can't set state for ${fileId}`);
    }
    this.files[fileId] = { ...this.files[fileId], ...attributes };
  }

  get #loadingStatus() {
    return this.uppy.getPlugin('LoadingStatus');
  }

  #getFile(id) {
    return this.files[id];
  }

  #setFile(file) {
    this.files[file.id] = file;
  }

  #temporaryFileMeta(meta) {
    return {
      ...meta,
      name: `${uuid()}_${sanitizeFileName(meta.name)}`,
      tempfile: true
    };
  }

  #uploadRemoteFile = async (file) => {
    this.resetUploaderState(file.id);

    this.#loadingStatus?.show();
    this.#setFile(file);

    if (this.#getFile(file.id).serverToken) {
      return this.#connectToServerSocket(this.#getFile(file.id));
    }
    const serverToken = await this.#queueRequestSocketToken(this.#getFile(file.id));
    this.#setFileState(file.id, { serverToken });

    return this.#connectToServerSocket(this.#getFile(file.id));
  };

  resetUploaderState = (fileId) => {
    if (this.sockets[fileId]) {
      this.sockets[fileId].close();
      this.sockets[fileId] = null;
    }

    if (this.files[fileId]) {
      this.files[fileId] = null;
    }
  };

  // See companion architecture: https://github.com/transloadit/uppy/blob/main/packages/%40uppy/companion/ARCHITECTURE.md#websockets
  #requestSocketToken = async (file) => {
    const Client = file.remote.providerOptions.provider ? Provider : RequestClient;
    const client = new Client(this.uppy, file.remote.providerOptions);
    const opts = { ...this.opts };

    if (file.tus) {
      // Install file-specific upload overrides.
      Object.assign(opts, file.tus);
    }

    const res = await client.post(file.remote.url, {
      ...file.remote.body,
      endpoint: opts.endpoint,
      uploadUrl: opts.uploadUrl,
      protocol: 'tus',
      size: file.data.size,
      headers: opts.headers,
      metadata: this.#temporaryFileMeta(file.meta),
    });

    return res.token;
  };

  #connectToServerSocket(file) {
    return new Promise((resolve, reject) => {
      let queuedRequest;

      const token = file.serverToken;
      const host = getSocketHost(file.remote.companionUrl);
      const socket = new Socket({ target: `${host}/api/${token}` });

      this.sockets[file.id] = socket;

      socket.on('error', (errData) => {
        this.resetUploaderState(file.id);

        queuedRequest.done();
        reject(new Error(errData.error.message, { cause: errData.error }));
      });

      socket.on('success', (data) => {
        this.resetUploaderState(file.id);

        queuedRequest.done();
        resolve(data);
      });

      queuedRequest = this.requests.run(() => {
        socket.open();

        return () => this.resetUploaderState(file.id);
      });
    });
  }

  install() {
    this.uppy.log('Installing RemoteImageDownloader');
  }
}
